import type {
  AccessGroup,
  ClientScopeKey,
  DemoClientsKey,
  OwnerGroupRef,
  ServiceTeamRef,
} from "types/interfaces/access";

/** Which clients a person's access group shows, as the client form needs it. */
export interface ClientRule {
  /** The group's slug: Super Admin sees every client. */
  slug: string;
  teamSlug: string | null;
  teamName: string | null;
  scope: ClientScopeKey;
  demo: DemoClientsKey;
}

/** What decides which groups see a client: its teams and its demo mark. */
export interface ClientMarks {
  teamSlugs: string[];
  isDemo: boolean;
}

/** Why a person named on a client does not see it: their group sees its team's
 * clients and the team does not work this one, or the client's demo mark is the
 * wrong one for the group. */
export type HiddenReason = "team" | "demo" | "not_demo";

export function marksOf(client: {
  service_teams?: ServiceTeamRef[];
  is_demo?: boolean;
}): ClientMarks {
  return {
    teamSlugs: (client.service_teams ?? []).map((team) => team.slug),
    isDemo: Boolean(client.is_demo),
  };
}

export function ruleOfGroup(group: AccessGroup): ClientRule {
  return {
    slug: group.slug,
    teamSlug: group.team,
    teamName: group.team_name,
    scope: group.client_scope,
    demo: group.demo_clients,
  };
}

export function ruleOfOwner(ref: OwnerGroupRef): ClientRule {
  return {
    slug: ref.slug,
    teamSlug: ref.team?.slug ?? null,
    teamName: ref.team?.name ?? null,
    scope: ref.client_scope,
    demo: ref.demo_clients,
  };
}

/** Why a person whose group has `rule`, named on the client, does not see it;
 * null when they do. Without a group nothing is said - every page is closed to
 * them anyway. */
export function hiddenReason(
  rule: ClientRule | null | undefined,
  client: ClientMarks,
): HiddenReason | null {
  if (!rule || rule.slug === "super_admin") return null;
  if (client.isDemo && rule.demo === "exclude") return "demo";
  if (!client.isDemo && rule.demo === "only") return "not_demo";
  // A team bounds the assigned clients as well as a team's own.
  const teamBound =
    rule.scope === "team" || (rule.scope === "assigned" && !!rule.teamSlug);
  if (teamBound && !(rule.teamSlug && client.teamSlugs.includes(rule.teamSlug)))
    return "team";
  return null;
}

export function hiddenText(reason: HiddenReason, rule: ClientRule): string {
  if (reason === "team")
    return `${rule.teamName ?? "Their team"} is not a service team on this client.`;
  if (reason === "demo") return "Their access group leaves demo clients out.";
  return "Their access group sees demo clients only.";
}

/** A person whose team is not among the client's service teams. */
export function teamMissing(
  rule: ClientRule | null | undefined,
  client: ClientMarks,
): boolean {
  return Boolean(rule?.teamSlug && !client.teamSlugs.includes(rule.teamSlug));
}

/** When a team was put on a client, as the Ownership step says it: "Added
 * October 30, 2026"; null for the teams carried over from the client labels,
 * whose date nobody noted. */
export function teamAddedLabel(
  since: string | null | undefined,
): string | null {
  if (!since) return null;
  const date = new Date(since);
  if (Number.isNaN(date.getTime())) return null;
  const day = date.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
  });
  return `Added ${day}`;
}

/** What an owner's Team column says: their group's team, or Super Admin - who
 * belongs to no team and sees every client; null for nothing to say. */
export function ownerTeamLabel(
  group: OwnerGroupRef | null | undefined,
): string | null {
  if (!group) return null;
  if (group.slug === "super_admin") return group.name || "Super Admin";
  return group.team?.name ?? null;
}
